// src/pages/wallet/walletCollection/index.jsx
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import BreadCrumbComp from "../../../components/breadCrumb";
import SimpleSelect from "../../../components/selects/simpleSelect";
import { icons } from "../../../constants";
import { useForm } from "react-hook-form";
import {
  getCurrentMonth,
  getCurrentYear,
  getMonthName,
} from "../../../utils/dateUtils";
import { months } from "../../../constants/selectData";
import { buildOpenClosedSelect } from "../../../services/dateService";
import ExcelUploadField from "../../../components/excelUploadField";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LaddaButton from "../../../components/buttons/laddaButton";
import SimpleTable from "../../../components/tables/simpleTable";
import VerificationModal from "../../../components/modals/verificationModal";
import { useSelector } from "react-redux";
import ToastNotification from "../../../components/toastNotification";
import { canManageWalletPayment } from "../../../constants/walletRoles";
import { walletCollectionColumns } from "./tableColumns";
import {
  getWalletCollectionHistoryDATA,
  postWalletCollectionUploadDATA,
  getWalletCollectionClientsDATA,
  getWalletCollectionInvoicesDATA,
  downloadWalletCollectionHistoryDATA,
} from "./walletCollectionCallApi";
import FormAutocomplete from "../../../components/selects/formAutocomplete";
import MultiSelect from "../../../components/selects/multiSelect";
import { getWalletLinesDATA } from "../walletCallApi";

const NULL_LINE_OPTION = "__NULL__";

const recaudoCategoryLabels = {
  notInVentas: "Factura no encontrada en ventas",
  duplicate: "Recaudo duplicado",
  duplicate_sql: "Recaudo duplicado",
  multi_pago_mismo_documento: "Pago adicional bloqueado",
  fila_total: "Fila de totales omitida",
  sin_consecutivo: "Documento sin número de factura",
  sin_invoice_en_bd: "Factura no encontrada en cartera",
  sin_wallet_en_bd: "Factura sin cartera del periodo",
  error_sql: "Error al guardar el recaudo",
  other: "No insertada"
};

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 2
  });
};

const recaudoReasonLabel = (row) => {
  const category = row?.category || "other";
  if (category === "notInVentas") {
    return "La factura no se encontró en las ventas/cartera cargadas. Revisa COM-NUMERO-VT o carga primero la cartera con ventas.";
  }
  if (category === "duplicate" || category === "duplicate_sql") {
    return "Ya existe un recaudo con la misma factura, recibo y valor.";
  }
  if (category === "multi_pago_mismo_documento") {
    return "El pago parece ser adicional sobre el mismo documento, pero el sistema no lo pudo guardar. Reporta este caso a soporte.";
  }
  if (category === "fila_total") {
    return "El sistema omitió una fila de totales del archivo.";
  }
  if (category === "sin_consecutivo") {
    return "No se pudo identificar el número de factura en COM-NUMERO-VT.";
  }
  if (category === "sin_invoice_en_bd" || category === "sin_wallet_en_bd") {
    return "La factura no tiene cartera asociada en el periodo seleccionado.";
  }
  return row?.reason || "No se pudo insertar esta fila.";
};

const RecaudoNotInsertedDetailsModal = ({ open, onClose, feedback }) => {
  if (!open || !feedback) return null;

  const rows = feedback.notInserted || [];
  const byCategory = feedback.byCategory || {};
  const categories = Object.keys(byCategory);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        <Typography variant="h6" component="span" color="error.main">
          Detalle de filas no insertadas
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          El recaudo se cargó, pero <strong>{feedback.notInsertedCount}</strong> fila(s) no se
          insertaron. Revisa los motivos, corrige el Excel o la cartera relacionada y vuelve a
          cargar el archivo si aplica.
        </Typography>

        {categories.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            {categories.map((category) => (
              <Paper key={category} variant="outlined" sx={{ px: 1.5, py: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {recaudoCategoryLabels[category] || recaudoCategoryLabels.other}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {byCategory[category]?.count || 0} fila(s)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatCurrency(byCategory[category]?.sum)}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Fila Excel</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Factura</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Documento</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Recibo</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Valor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Motivo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={`${row.excelRow || idx}-${row.com_numero_vt || ""}-${idx}`}>
                  <TableCell>{row.excelRow || "—"}</TableCell>
                  <TableCell>{row.invoice_code || "—"}</TableCell>
                  <TableCell>{row.com_numero_vt || "—"}</TableCell>
                  <TableCell>{row.receipt || "—"}</TableCell>
                  <TableCell>{formatCurrency(row.paid_amount)}</TableCell>
                  <TableCell sx={{ minWidth: 280 }}>{recaudoReasonLabel(row)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <LaddaButton variant="contained" onClick={onClose}>
          <div className="HContainer align-center">
            <p className="button_text">Entendido</p>
          </div>
        </LaddaButton>
      </DialogActions>
    </Dialog>
  );
};

function normalizeHistoryLineIds(historyLineIds) {
  if (!Array.isArray(historyLineIds)) return [];
  return Array.from(
    new Set(
      historyLineIds
        .map((value) => String(value).trim())
        .filter((value) => value && value !== "null")
    )
  );
}

const WalletCollection = () => {
  const [recaudoFile, setRecaudoFile] = useState(null);
  const [uploadKey, setUploadKey] = useState(0);
  const [openVerification, setOpenVerification] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [verificationText, setVerificationText] = useState(
    "¿Estás seguro de cargar el archivo de Recaudo?"
  );
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastSeverity, setToastSeverity] = useState("success");
  const [toastMessage, setToastMessage] = useState("");
  const [recaudoFeedback, setRecaudoFeedback] = useState(null);
  const [openRecaudoDetails, setOpenRecaudoDetails] = useState(false);

  // table
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(15);

  // Filtros: valores del formulario (lo que el usuario elige)
  const [clientOptions, setClientOptions] = useState([]);
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [lineOptions, setLineOptions] = useState([
    { name: "Sin línea de negocio", value: NULL_LINE_OPTION }
  ]);
  // Filtros aplicados: solo se consulta al backend cuando el usuario pulsa "Filtrar"
  const [appliedFilters, setAppliedFilters] = useState(null);

  const defaultHistoryValues = {
    historyYear: getCurrentYear(),
    historyMonth: getCurrentMonth(),
    historyLineIds: [],
    historyClient: { value: null, name: "Todos" },
    historyInvoice: { value: null, name: "Todas" },
  };

  const { control, handleSubmit, getValues, watch, reset, setValue } = useForm({
    defaultValues: {
      year: getCurrentYear(),
      month: getCurrentMonth(),
      ...defaultHistoryValues,
    },
  });

  const historyYear = watch("historyYear");
  const historyMonth = watch("historyMonth");
  const historyLineIds = watch("historyLineIds");
  const historyClient = watch("historyClient");
  const historyInvoice = watch("historyInvoice");

  const userData = useSelector((state) => state.userData.userData);
  const navigate = useNavigate();

  const isWalletAdmin = canManageWalletPayment(userData.roleList);

  const showSuccess = (msg) => {
    setSubmitError("");
    setSubmitSuccess(msg);
    setToastSeverity("success");
    setToastMessage(msg);
    setShowToast(true);
  };

  const showError = (msg) => {
    setSubmitSuccess("");
    setSubmitError(msg);
    setToastSeverity("error");
    setToastMessage(msg);
    setShowToast(true);
  };

  const resetUploadForm = () => {
    setRecaudoFile(null);
    setUploadKey((k) => k + 1);
  };

  // Cargar opciones de clientes
  useEffect(() => {
    getWalletCollectionClientsDATA(setClientOptions, userData.id);
  }, []);

  useEffect(() => {
    getWalletLinesDATA((options) => {
      const filteredOptions = (options || []).filter((opt) => opt?.value !== "null");
      setLineOptions([{ name: "Sin línea de negocio", value: NULL_LINE_OPTION }, ...filteredOptions]);
    }, userData.id);
  }, []);

  // Cargar opciones de facturas cuando cambian año/mes/línea del formulario
  useEffect(() => {
    if (historyYear && historyMonth) {
      getWalletCollectionInvoicesDATA(
        historyYear,
        historyMonth,
        setInvoiceOptions,
        normalizeHistoryLineIds(historyLineIds),
        userData.id
      );
    }
  }, [historyYear, historyMonth, historyLineIds]);

  useEffect(() => {
    setValue("historyInvoice", { value: null, name: "Todas" });
  }, [historyLineIds, setValue]);

  // Cargar historial solo cuando hay filtros aplicados (usuario pulsó "Filtrar")
  useEffect(() => {
    if (!appliedFilters || !appliedFilters.year || !appliedFilters.month) return;

    getWalletCollectionHistoryDATA(
      appliedFilters.year,
      appliedFilters.month,
      historyPage,
      historyPageSize,
      setTableData,
      setHistoryTotal,
      setHistoryPage,
      appliedFilters.clientId ?? null,
      appliedFilters.invoiceCode ?? null,
      appliedFilters.lineIds ?? [],
      userData.id
    );
  }, [appliedFilters, historyPage, historyPageSize]);

  const handleApplyFilter = () => {
    const year = getValues("historyYear");
    const month = getValues("historyMonth");
    if (!year || !month) {
      showError("Selecciona año y mes para filtrar");
      return;
    }
    const clientIdValue =
      historyClient && typeof historyClient === "object"
        ? historyClient.value
        : historyClient;
    const invoiceCodeValue =
      historyInvoice && typeof historyInvoice === "object"
        ? historyInvoice.value
        : historyInvoice;
    setAppliedFilters({
      year,
      month,
      clientId: clientIdValue ?? null,
      invoiceCode: invoiceCodeValue ?? null,
      lineIds: normalizeHistoryLineIds(historyLineIds),
    });
    setHistoryPage(1);
  };

  const handleClearFilter = () => {
    reset({
      year: getValues("year"),
      month: getValues("month"),
      historyYear: getCurrentYear(),
      historyMonth: getCurrentMonth(),
      historyLineIds: [],
      historyClient: { value: null, name: "Todos" },
      historyInvoice: { value: null, name: "Todas" },
    });
    setAppliedFilters(null);
    setTableData([]);
    setHistoryTotal(0);
    setHistoryPage(1);
  };

  // Submit (abrir modal)
  const onSubmit = (formData) => {
    setSubmitError("");
    setSubmitSuccess("");

    const missing = [];
    if (!formData.year) missing.push("Año");
    if (!formData.month) missing.push("Mes");
    if (!recaudoFile) missing.push("Recaudo (Excel)");

    if (missing.length) {
      showError(`Faltan campos obligatorios: ${missing.join(", ")}`);
      return;
    }

    // Validar que el archivo sea Excel
    const fileName = recaudoFile?.name || "";
    const isValidExtension = /\.(xlsx|xls)$/i.test(fileName);
    if (!isValidExtension) {
      showError("El archivo debe ser un Excel (.xlsx o .xls)");
      return;
    }

    const payload = {
      year: formData.year,
      month: formData.month,
      documentType: "Recaudo",
      files: {
        recaudo: recaudoFile,
      },
    };

    const monthName = getMonthName(Number(formData.month));
    setVerificationText(
      `¿Deseas cargar el recaudo de ${monthName} ${formData.year}?`
    );
    setPendingPayload(payload);
    setOpenVerification(true);
  };

  // Confirm modal
  const handleConfirmGenerate = async () => {
    if (!pendingPayload || isSubmitting) {
      setOpenVerification(false);
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setSubmitSuccess("");
    setRecaudoFeedback(null);
    setOpenRecaudoDetails(false);

    await postWalletCollectionUploadDATA(
      { ...pendingPayload, userId: userData.id },
      (resp) => {
        const data = resp?.data ?? resp;
        const monthName = getMonthName(Number(pendingPayload.month));
        const year = pendingPayload.year;

        // --- Validación éxito (comentado: descomentar para depurar) ---
        // console.log("[RECAUDO] Éxito:", { data, stats: data?.stats });

        // Mejorar mensaje de éxito con estadísticas
        const stats = data?.stats || {};
        const statsMsg = [];
        if (stats.paymentsCreated !== undefined) {
          statsMsg.push(`${stats.paymentsCreated} recaudos creados`);
        }
        if (stats.notInVentas !== undefined && stats.notInVentas > 0) {
          statsMsg.push(`${stats.notInVentas} no encontrados en ventas`);
        }
        if (stats.paymentsWithoutWallet !== undefined && stats.paymentsWithoutWallet > 0) {
          statsMsg.push(`${stats.paymentsWithoutWallet} sin cartera (solo ventas)`);
        }
        if (stats.duplicates !== undefined && stats.duplicates > 0) {
          statsMsg.push(`${stats.duplicates} duplicados omitidos`);
        }

        const fb = stats.feedback;
        if (fb?.notInsertedCount > 0) {
          setRecaudoFeedback(fb);
          console.warn("[RECAUDO] Filas NO insertadas:", {
            total: fb.notInsertedCount,
            suma: fb.notInsertedSum,
            porCategoria: fb.byCategory,
            detalle: fb.notInserted
          });
        }
        if (fb?.insertedOtherPeriodCount > 0) {
          console.info("[RECAUDO] Insertadas con fecha de pago en otro mes:", fb.insertedOtherPeriod);
        }

        const notInsertedMsg =
          fb?.notInsertedCount > 0 ? ` ${fb.notInsertedCount} filas no insertadas.` : "";
        showSuccess(`Recaudo ${monthName} ${year} cargado con éxito.${notInsertedMsg}`);

        if (appliedFilters && appliedFilters.year && appliedFilters.month) {
          setHistoryPage(1);
          setAppliedFilters((prev) => (prev ? { ...prev } : null));
        }

        setOpenVerification(false);
        setPendingPayload(null);
        resetUploadForm();
        setIsSubmitting(false);
      },
      (err) => {
        const errorData = err?.response?.data || {};
        let msg =
          errorData.message ||
          errorData.error ||
          "No se pudo subir el recaudo. Revisa el archivo e inténtalo de nuevo.";

        // Cuando no hay response (502, CORS, red): mensaje diciente para el usuario
        if (!err?.response) {
          if (err?.code === "ERR_NETWORK" || /network error/i.test(String(err?.message))) {
            msg =
              "No se pudo conectar con el servidor (error de red o servidor no disponible). Intente de nuevo en unos minutos.";
          } else {
            msg =
              "No se pudo completar la subida. El servidor no respondió correctamente. Intente más tarde.";
          }
        }

        // Resumen para producción (siempre visible en consola para depurar 502/CORS)
        console.warn("[RECAUDO] Error en pantalla:", {
          code: err?.code,
          message: err?.message,
          hasResponse: !!err?.response,
          status: err?.response?.status
        });

        showError(msg);
        setOpenVerification(false);
        setPendingPayload(null);
        setIsSubmitting(false);
      }
    );
  };

  const handleCloseModal = () => {
    if (isSubmitting) return;
    setOpenVerification(false);
    setPendingPayload(null);
  };

  // Handler para cambio de página
  const handlePageChange = (newPage, newPageSize) => {
    setHistoryPage(newPage);
    if (newPageSize) setHistoryPageSize(newPageSize);
  };

  // Handler para descargar tabla (usa los filtros aplicados)
  const handleDownloadTable = async () => {
    if (!appliedFilters || !appliedFilters.year || !appliedFilters.month) {
      showError("Aplica primero un filtro (año y mes) y luego descarga");
      return;
    }

    try {
      await downloadWalletCollectionHistoryDATA(
        appliedFilters.year,
        appliedFilters.month,
        appliedFilters.clientId ?? null,
        appliedFilters.invoiceCode ?? null,
        appliedFilters.lineIds ?? [],
        userData.id
      );
    } catch (err) {
      showError("No se pudo descargar el archivo. Inténtalo de nuevo.");
    }
  };

  return (
    <>
      <ToastNotification
        severity={toastSeverity}
        summary={toastMessage}
        detail=""
        position="top-center"
        showToast={showToast}
        setShowToast={setShowToast}
      />

      <BreadCrumbComp />
      <div className="searchTitle">
        <h1>DASHBOARD GESTIÓN FINANCIERA Y CONTABLE</h1>
      </div>

      {/* Formulario de carga */}
      {isWalletAdmin && (
        <div className="table-container">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap"
            }}
          >
            <h1 className="title-bio-details-chard" style={{ margin: 0 }}>
              Días Cartera
            </h1>
            <LaddaButton
              variant="contained"
              onClick={() => navigate("/wallet/products")}
            >
              <div className="HContainer align-center">
                <img className="btn-icon mr-10" src={icons.gear} alt="icon" />
                <p className="button_text">Administrar productos</p>
              </div>
            </LaddaButton>
          </div>
          <p>Selecciona el Mes y Año al que corresponde la Cartera</p>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <p className="blueLabelForm">Mes:</p>
              <SimpleSelect
                margin={"0"}
                name="month"
                control={control}
                options={months}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <p className="blueLabelForm">Año:</p>
              <SimpleSelect
                margin={"0"}
                name="year"
                control={control}
                options={buildOpenClosedSelect()}
              />
            </Grid>
          </Grid>

          <h1 className="title-bio-details-chard">Carga de Documento</h1>
          <p>Haz clic en «Seleccionar archivo» para cargar el recaudo</p>

          <div className="info-card">
            <img
              className="btn-icon-32 mr-10"
              src={icons.info_blue}
              alt="icon"
            />
            <span>
              Para calcular la cartera del mes actual debes subir el documento del Recaudo.
              Una vez cargado, podrás visualizar y descargar cada uno.
            </span>
          </div>

          <br />

          <Grid container spacing={2} key={uploadKey}>
            <Grid item xs={12}>
              <ExcelUploadField
                label="Recaudo"
                onFileChange={(file) => {
                  setRecaudoFile(file);
                  setSubmitError("");
                }}
                disabled={isSubmitting}
              />
            </Grid>
          </Grid>

          {/* Mensajes debajo */}
          {submitError ? (
            <div
              style={{
                marginTop: "14px",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid rgba(198,40,40,.35)",
                background: "rgba(198,40,40,.08)",
                color: "#c62828",
                fontSize: "13px",
              }}
            >
              {submitError}
            </div>
          ) : null}

          {submitSuccess ? (
            <div
              style={{
                marginTop: "14px",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid rgba(46,125,50,.35)",
                background: "rgba(46,125,50,.08)",
                color: "#2e7d32",
                fontSize: "13px",
              }}
            >
              <span>{submitSuccess}</span>
              {recaudoFeedback?.notInsertedCount > 0 ? (
                <button
                  type="button"
                  onClick={() => setOpenRecaudoDetails(true)}
                  style={{
                    marginLeft: "8px",
                    padding: 0,
                    border: 0,
                    background: "transparent",
                    color: "#1565c0",
                    font: "inherit",
                    fontWeight: 600,
                    textDecoration: "underline",
                    cursor: "pointer"
                  }}
                >
                  Ver detalles
                </button>
              ) : null}
            </div>
          ) : null}

          <div
            className="HContainer j-center w-complete"
            style={{ marginTop: "16px" }}
          >
            <LaddaButton
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting}
            >
              <div className="HContainer align-center">
                <p className="button_text">
                  {isSubmitting ? "Enviando..." : "Generar Días Carteras"}
                </p>
              </div>
            </LaddaButton>
          </div>
        </div>
      )}

      {/* Tabla historial */}
      <div className="table-container">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <h1 className="title-bio-details-chard" style={{ margin: 0 }}>
            Historial Días Cartera
          </h1>
          <LaddaButton variant="contained" onClick={handleDownloadTable}>
            <div className="HContainer align-center">
              <img
                className="btn-icon-16 mr-10"
                src={icons.downloadIcon}
                alt="download"
              />
              <p className="button_text">Descargar Días Cartera</p>
            </div>
          </LaddaButton>
        </div>

        <p style={{ marginBottom: 8 }}>
          Filtra por mes y año según la <strong>fecha de pago</strong> del recaudo.
        </p>
        <Grid container spacing={1} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={2}>
            <p className="blueLabelForm">Mes:</p>
            <SimpleSelect
              margin={"0"}
              name="historyMonth"
              control={control}
              options={months}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <p className="blueLabelForm">Año:</p>
            <SimpleSelect
              margin={"0"}
              name="historyYear"
              control={control}
              options={buildOpenClosedSelect()}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <p className="blueLabelForm">Cliente:</p>
            <FormAutocomplete
              margin={"0"}
              name="historyClient"
              control={control}
              options={[{ value: null, name: "Todos" }, ...clientOptions]}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <p className="blueLabelForm">Nro de Factura:</p>
            <FormAutocomplete
              margin={"0"}
              name="historyInvoice"
              control={control}
              options={[{ value: null, name: "Todas" }, ...invoiceOptions]}
            />
          </Grid>
 
          
        
          <Grid item xs={12} md={3}>
            <p className="blueLabelForm">Línea de negocio:</p>
            <MultiSelect
              margin={"0"}
              name="historyLineIds"
              control={control}
      
              options={lineOptions}
              isMultiple={true}
            />
          </Grid>

          <Grid item xs={12} md={1}>
            <div
              className="IHContainer j-end"
              style={{ height: "100%", alignItems: "flex-end", justifyContent: "flex-end" }}
            >
              <LaddaButton
                variant="contained"
                onClick={handleApplyFilter}
                margin={"0 0 0 10px"}
                width={"52px"}
              >
                <img
                  className="btn-icon"
                  src={icons.filterWhite}
                  alt="icon"
                />
              </LaddaButton>
              <LaddaButton
                variant="contained"
                onClick={handleClearFilter}
                margin={"0 0 0 10px"}
              >
                <img
                  className="btn-icon"
                  src={icons.clearFilterWhite}
                  alt="icon"
                />
              </LaddaButton>
            </div>
          </Grid>
        </Grid>

        <br />

        <SimpleTable
          data={tableData}
          columns={walletCollectionColumns}
          isPaginated={true}
          showItemCount={true}
          serverPagination={true}
          totalRecords={historyTotal}
          rowsPerPage={historyPageSize}
          currentPage={historyPage}
          onPageChange={handlePageChange}
          scrollHeight="1600px"
        />
      </div>

      <VerificationModal
        open={openVerification}
        handleClose={handleCloseModal}
        alertText={verificationText}
        onConfirm={handleConfirmGenerate}
      />
      <RecaudoNotInsertedDetailsModal
        open={openRecaudoDetails}
        onClose={() => setOpenRecaudoDetails(false)}
        feedback={recaudoFeedback}
      />
    </>
  );
};

export default WalletCollection;
